import PropTypes from "prop-types";
import React from "react";
import classNames from "classnames";
import Dispatcher from "Dispatcher";
import ActiveSectionActions from "data/ActiveSectionActions";
import ActiveSectionStore from "data/ActiveSectionStore";
import FormStore from "data/meta/FormStore";
import NavigatorStore from "data/NavigatorStore";
import userStore from "data/userStore";
import StereotypesStore from "data/StereotypesStore";
import NavigationActions from "data/NavigationActions";
import MapButton from "lib/mapping/mappingbutton/MapButton";
import { recordIdType } from "data/util/typedefs";
import useFluxStore from "@armus/use-flux-store";
import SuperRecordActions from "data/SuperRecordActions";
import ClientId from "data/ClientId";
import PrintButton from "lib/dataentry/lib/print/PrintButton";
import EntityName from "data/util/EntityName";
import entityMetaStore from "data/meta/entityMetaStore";
import HybridConfig from "lib/HybridConfig";

export class ActionBar extends React.Component {
    constructor(props) {
        super(props);
        this.onSectionChange = this.onSectionChange.bind(this);
        this.onVersionSelect = this.onVersionSelect.bind(this);
    }

    onSectionChange(event) {
        event.preventDefault();
        this.props.onSectionChange(event.target.value);
    }

    onVersionSelect(event) {
        event.preventDefault();
        this.props.onVersionSelect(event.target.value);
    }

    render() {
        const {
            fluxContext,
            onSave,
            onCancel,
            sectionNames,
            currentSection,
            allowSectionJump,
            allowSave,
            showMapButton,
            showPrintButton,
            entityKey,
            recordId,
            allowCancel,
            availableVersions,
            activeVersion,
            showVersionDropdown,
        } = this.props;

        const saveBtnClasses = classNames(
            "btn",
            "btn-save",
            "actionSave",
        );

        const sectionJump = !allowSectionJump && "disabled",
              save = !allowSave && "disabled",
              cancel = !allowCancel && "disabled";
        return (
            <div className="form-inline">
                <button
                    onClick={onSave}
                    type="button"
                    className={saveBtnClasses}
                    disabled={save}
                ><span></span> Save
                </button>
                {showMapButton && <MapButton
                    entityKey={entityKey}
                    recordId={recordId}
                    fluxContext={fluxContext}
                />}
                {showPrintButton && <PrintButton
                    entityKey={entityKey}
                    recordId={recordId}
                />}
                <button onClick={onCancel}
                        type="button"
                        className="btn cancel btn-cancel actionCancel"
                        disabled={cancel}><span></span> Cancel
                </button>

                <div className="form-impl">
                    { showVersionDropdown && availableVersions.length > 1 &&
                    <div className="from-group version-section"
                        data-control-type="dropdown">
                        <label>Version:</label>
                        {" "}
                        <select onChange={this.onVersionSelect}
                                value={activeVersion}
                                className="form-control version-select" >
                            {availableVersions.map(it =>
                                <option key={it.key} value={it.key}>{it.name}</option>
                            )}
                        </select>
                    </div> }
                </div>
                
                { allowSectionJump && sectionNames.length > 1 &&
                  <div className="pull-right from-group form-section"
                       data-control-type="dropdown">
                      <label>Jump to:</label>
                      {" "}
                      <select onChange={this.onSectionChange}
                              value={currentSection}
                              className="form-control section-select" disabled={sectionJump}>
                          {sectionNames.map(n =>
                              <option key={n}>{n}</option>
                          )}
                      </select>
                  </div> }
            </div>
        );
    }
}

ActionBar.propTypes = {
    fluxContext: PropTypes.string.isRequired,
    sectionNames: PropTypes.arrayOf(
        PropTypes.string,
    ).isRequired,
    currentSection: PropTypes.string,
    allowSectionJump: PropTypes.bool,
    allowSave: PropTypes.bool,
    showMapButton: PropTypes.bool,
    showPrintButton: PropTypes.bool,
    allowCancel: PropTypes.bool,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    onSectionChange: PropTypes.func,
    availableVersions: PropTypes.array,
    activeVersion: PropTypes.string,
    entityKey: PropTypes.string.isRequired,
    recordId: recordIdType.isRequired,
    onVersionSelect: PropTypes.func,
    showVersionDropdown: PropTypes.bool,
};

export const FluxActionBar = (props) => {
    const {
        entityKey,
        recordId,
        fluxContext,
        versionDropdownEnabled,
        allowSave,
        allowCancel,
        isModal,
        onSave,
    } = props;
    const state = useFluxStore(
        () => {
            const state = {
                fluxContext,
                showMapButton: !isModal,
                showPrintButton: !ClientId.is(recordId)
                    && HybridConfig.get("export.enablePrintToPdf", true)
                    && entityMetaStore.isRootRecord(entityKey) // BE CAREFUL : this ensures an "is lookup" is irrelevant, so one isn't made
                    && userStore.canPrint(new EntityName(entityKey).implKey),
                sectionNames: FormStore.getSectionNames(entityKey),
                currentSection: ActiveSectionStore.getCurrentSection(fluxContext),
                onSectionChange: section => {
                    Dispatcher.contextDispatch(fluxContext, {
                        type: ActiveSectionActions.JUMP_TO_SECTION,
                        section: section,
                    });
                },
                onSave: () => {
                    if (allowSave) {
                        onSave && onSave();
                    }
                },
                onCancel: () => allowCancel && Dispatcher.contextDispatch(fluxContext, {
                    type: SuperRecordActions.CANCEL,
                }),
            };
            const {
                magic,
                record,
            } = NavigatorStore.getState(fluxContext);
            if (versionDropdownEnabled && record != null && magic.id === record.id) {
                state.onVersionSelect = entityKey => {
                    Dispatcher.contextDispatch(fluxContext, {
                        type: NavigationActions.SELECT_RECORD,
                        entityKey,
                        recordId: record.id,
                    });
                };
                state.activeVersion = record.key;
                state.availableVersions = StereotypesStore
                    .getStereotypeByKey(magic.key)
                    .implementations;
                state.showVersionDropdown = state.availableVersions.length > 1;
            } else {
                state.showVersionDropdown = false;
            }
            return state;
        },
        [
            userStore,
            entityMetaStore,
            ActiveSectionStore,
            FormStore,
            NavigatorStore,
        ],
        [entityKey, fluxContext, versionDropdownEnabled, allowSave, allowCancel]
    );
    return <ActionBar {...props} {...state} />;
};

FluxActionBar.propTypes = {
    fluxContext: PropTypes.string.isRequired,
    isModal: PropTypes.bool,
    versionDropdownEnabled: PropTypes.bool,
    allowSectionJump: PropTypes.bool,
    allowSave: PropTypes.bool,
    allowCancel: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    entityKey: PropTypes.string.isRequired,
    recordId: recordIdType.isRequired,
};



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/dataentryform/views/ActionBar.js